import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { ClerkProvider } from '@clerk/clerk-react';
import { esES } from '@clerk/localizations';
import { ThemeProvider, useTheme } from './components/ThemeProvider';
import { dark } from '@clerk/themes';
import { RouterProvider } from 'react-router-dom';
import { router } from './router';
import ConfigurationGuide from './components/ConfigurationGuide.tsx';
import './index.css';

// Wrapper component to handle Clerk theme updates
const ClerkThemeWrapper = ({ children }: { children: React.ReactNode }) => {
  const { theme } = useTheme();
  const [isDark, setIsDark] = useState(
    theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
  );

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      setIsDark(theme === 'dark' || (theme === 'system' && media.matches));
    };

    handleChange();
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, [theme]);

  return (
    <ClerkProvider 
      publishableKey={PUBLISHABLE_KEY}
      localization={esES}
      appearance={{
        baseTheme: isDark ? dark : undefined,
        elements: {
          rootBox: "!w-full",
          card: "!w-full !max-w-[550px] min-[430px]:!min-w-[430px]"
        }
      }}
    >
      {children}
    </ClerkProvider>
  );
};

// Check environment configuration
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const DATABASE_URL = import.meta.env.VITE_DATABASE_URL;

const isClerkConfigured = PUBLISHABLE_KEY && 
  PUBLISHABLE_KEY !== 'pk_test_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' && 
  PUBLISHABLE_KEY.startsWith('pk_');

const isDatabaseConfigured = DATABASE_URL && 
  DATABASE_URL !== 'postgresql://username:password@ep-xxxxxxxxx.us-east-1.aws.neon.tech/neondb?sslmode=require' && 
  DATABASE_URL.startsWith('postgresql://');

// Show configuration guide if environment is not properly set up
if (!isClerkConfigured || !isDatabaseConfigured) {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ConfigurationGuide 
        missingClerk={!isClerkConfigured}
        missingDatabase={!isDatabaseConfigured}
      />
    </StrictMode>
  );
} else {
  // Only initialize Clerk if properly configured
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
        <ClerkThemeWrapper>
          <RouterProvider router={router} />
        </ClerkThemeWrapper>
      </ThemeProvider>
    </StrictMode>
  );
}
